/**
 * THE TRANSCRIPT DOCUMENT
 *
 * A transcript is a structured document, not a string that gets compared to an
 * earlier string. Deletions are recorded IN the document as a mark on the
 * words themselves, so struck wording keeps its exact position and can never
 * be lost, reconstructed or mis-derived.
 *
 *   { type: 'doc', content: [
 *       { type: 'paragraph', content: [
 *           { type: 'text', text: 'We went to the studio ' },
 *           { type: 'text', text: 'on Tuesday', marks: [{ type: 'deletion' }] },
 *           { type: 'text', text: '.' } ] } ] }
 *
 * Two marks exist:
 *   deletion  — original wording a reader has struck out. Still present.
 *   insertion — wording a reader added. Deleting it removes it outright,
 *               because it was never part of Portal's transcript.
 *
 * Stored in transcript_versions.marks (jsonb), alongside the readable text in
 * transcript_versions.content — so every existing reader of `content` (staff
 * view, word counts, downloads elsewhere) keeps working untouched, and rows
 * written before this existed simply open as plain paragraphs.
 */

export const DELETION = 'deletion'
export const INSERTION = 'insertion'

export interface TextNode {
  type: 'text'
  text: string
  marks?: { type: string; attrs?: Record<string, unknown> }[]
}

export interface ParagraphNode {
  type: 'paragraph'
  content?: TextNode[]
}

export interface TranscriptDoc {
  type: 'doc'
  content: ParagraphNode[]
}

export const EMPTY_DOC: TranscriptDoc = { type: 'doc', content: [{ type: 'paragraph' }] }

function hasMark(node: TextNode, name: string): boolean {
  return Boolean(node.marks?.some((mark) => mark.type === name))
}

/** A plain transcript becomes one paragraph per line. */
export function docFromText(text: string): TranscriptDoc {
  const lines = text.split('\n')
  return {
    type: 'doc',
    content: lines.map((line) =>
      line.length ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' },
    ),
  }
}

/** The transcript as it now reads: struck wording is not part of the text. */
export function textFromDoc(doc: TranscriptDoc): string {
  return doc.content
    .map((paragraph) =>
      (paragraph.content ?? [])
        .filter((node) => !hasMark(node, DELETION))
        .map((node) => node.text)
        .join(''),
    )
    .join('\n')
}

/** Every word, struck or not — used to locate a quoted passage. */
export function fullTextFromDoc(doc: TranscriptDoc): string {
  return doc.content
    .map((paragraph) => (paragraph.content ?? []).map((node) => node.text).join(''))
    .join('\n')
}

/**
 * A readable download: the transcript as it now reads, with struck wording
 * marked in place rather than quietly dropped.
 */
export function exportTextFromDoc(doc: TranscriptDoc): string {
  return doc.content
    .map((paragraph) =>
      (paragraph.content ?? [])
        .map((node) => {
          if (!hasMark(node, DELETION)) return node.text
          const trimmed = node.text.trim()
          return trimmed ? `[removed: ${trimmed}]` : ''
        })
        .join(''),
    )
    .join('\n')
}

/** True when any wording in the document is struck out. */
export function docHasDeletions(doc: TranscriptDoc): boolean {
  return doc.content.some((paragraph) =>
    (paragraph.content ?? []).some((node) => hasMark(node, DELETION)),
  )
}

/** Nothing but text nodes and paragraphs is accepted, whatever was stored. */
function validParagraph(value: unknown): ParagraphNode | null {
  const node = value as ParagraphNode | null
  if (!node || node.type !== 'paragraph') return null
  const content = Array.isArray(node.content)
    ? node.content.filter(
        (child): child is TextNode =>
          Boolean(child) &&
          (child as TextNode).type === 'text' &&
          typeof (child as TextNode).text === 'string',
      )
    : undefined
  return content?.length ? { type: 'paragraph', content } : { type: 'paragraph' }
}

/**
 * The stored document, or one built from the stored text.
 *
 * A row written before tracked changes existed has no marks, so its plain text
 * opens as ordinary paragraphs — nothing is lost and nothing is invented.
 */
export function parseDoc(marks: unknown, fallbackText: string): TranscriptDoc {
  const raw = typeof marks === 'string' ? safeParse(marks) : marks
  const doc = raw as TranscriptDoc | null

  if (doc && doc.type === 'doc' && Array.isArray(doc.content)) {
    const content = doc.content
      .map(validParagraph)
      .filter((paragraph): paragraph is ParagraphNode => paragraph !== null)
    if (content.length) return { type: 'doc', content }
  }

  return fallbackText ? docFromText(fallbackText) : EMPTY_DOC
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/**
 * Where a quoted passage sits in the readable text, preferring the occurrence
 * nearest to where it was when the comment was made. Returns -1 when the
 * passage can no longer be found — the comment is kept regardless.
 */
export function findQuote(text: string, quote: string, near: number): number {
  if (!quote) return -1
  const first = text.indexOf(quote)
  if (first === -1 || near < 0) return first

  let best = first
  let index = first
  while (index !== -1) {
    if (Math.abs(index - near) < Math.abs(best - near)) best = index
    index = text.indexOf(quote, index + 1)
  }
  return best
}
